import React, { useEffect, useState, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Button from '@mui/material/Button';
import { AnalysisOptions } from '../Widgets/AlertConfig/AnalysisOptions/AnalysisOptionsContainer';
import {
  Alert,
  AlertParam,
  ChecklistType,
  makeDefaultAlertParam,
} from '../Widgets/AlertConfig/helpers';
import {
  addNewLocationAlert,
  addNewSensorAlert,
  deleteLocationAlert,
  deleteSensorAlert,
  getLocationAlertConfig,
  getSensorAlertConfig,
  updateLocationAlertStatus,
  updateSensorAlertStatus,
} from '../services/apiService';
import {
  Analysisoptions,
  Analysisoptions1,
  LocationAlertParamCreate,
  SensorAlertParamCreate,
  VarName,
} from '../services/api';
import { themeProps } from '../styles/theme';
import { useAppDispatch } from '../state/store';
import { setAlertsById } from '../state/actions';
import AlertParamAccordion from '../Widgets/AlertConfig/AlertParamAccordion';

export interface AlertConfigProps {
  checkListId: string;
  checklistType: ChecklistType;
  params?: AlertParam[]; // already fetched param, mainly showing in sensorData (SensorConfig)
  setParams?: (value: AlertParam[]) => void; // update params showing in container (atm in SensorData)
}

// Grouped by isLocation to get correct api calls
const getApi = (isLocation: boolean) => {
  const locApiCalls = {
    fetch: getLocationAlertConfig,
    add: addNewLocationAlert,
    remove: deleteLocationAlert,
    changeStatus: updateLocationAlertStatus,
  };

  const sensorApiCalls = {
    fetch: getSensorAlertConfig,
    add: addNewSensorAlert,
    remove: deleteSensorAlert,
    changeStatus: updateSensorAlertStatus,
  };
  return isLocation ? locApiCalls : sensorApiCalls;
};

function AlertConfig({ checkListId, checklistType, params, setParams }: AlertConfigProps) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isLocation = checklistType === ChecklistType.location;
  const api = useMemo(() => getApi(isLocation), [isLocation]);

  const [alertParams, setAlertParams] = useState<AlertParam[]>([]);

  useEffect(() => {
    if (params) setAlertParams(params);
  }, [params]);

  const handleError = (err: unknown) =>
    enqueueSnackbar((err as { cause: string }).cause, { variant: 'error', persist: true });

  // update local storages
  const syncAlerts = (res: Alert) => {
    dispatch(setAlertsById([res]));
    setAlertParams(res.alerts);
    if (setParams) setParams(res.alerts);
  };

  // fetch param if missing in props
  useEffect(() => {
    if (params) {
      return undefined;
    }

    // isActive is there to ignore the result of an old request after the effect has been cleaned up.
    let isActive = true;

    api.fetch(checkListId).then((res) => {
      if (!isActive) {
        return;
      }

      dispatch(setAlertsById([res]));
      setAlertParams(res.alerts.length > 0 ? res.alerts : []);
    });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkListId, isLocation]);

  const updateAlertParam = (index: number, patch: Partial<AlertParam>) =>
    setAlertParams((prev) =>
      prev.map((p, i) => (i === index ? ({ ...p, ...patch } as AlertParam) : p))
    );

  const updateAnalysisOptions = (index: number, patch: Partial<AnalysisOptions>) =>
    setAlertParams((prev) =>
      prev.map((p, i) =>
        i === index
          ? ({ ...p, analysisOptions: { ...p.analysisOptions, ...patch } } as AlertParam)
          : p
      )
    );

  const addAlertParam = () =>
    setAlertParams((prev) => {
      const usedIds = new Set(
        prev.map(({ alertId }) => alertId).filter((id): id is number => id !== undefined)
      );

      // find the lowest unused alertId instead. For example, if the existing IDs are:0,1,3,5. the new row gets alertId as 2
      let nextAlertId = 0;

      while (usedIds.has(nextAlertId)) {
        nextAlertId += 1;
      }

      return [
        ...prev,
        {
          ...makeDefaultAlertParam(isLocation),
          alertId: nextAlertId,
        },
      ];
    });

  // NOTE: this intersection + cast exists because the location/sensor create
  // payload shapes aren't actually unified at the type level. If those two
  // shapes diverge (e.g. a field required on one but not the other), this
  // cast will hide the mismatch instead of surfacing a type error. Worth
  // revisiting if the two APIs continue to drift.
  type CreateParam = LocationAlertParamCreate & SensorAlertParamCreate;

  const buildCreateParam = (alertParam: AlertParam): CreateParam =>
    ({
      alertMethods: alertParam.alertMethods,
      analysisOptions: alertParam.analysisOptions as Analysisoptions & Analysisoptions1,
      uiOptions: alertParam.uiOptions ?? null,
    } as CreateParam);

  const handleSubmit = (alertParam: AlertParam) => {
    const { analysisOptions } = alertParam;

    // narrows out the WiFiConf variants, which don't have varName
    const selectedVarName =
      'varName' in analysisOptions ? analysisOptions.varName : VarName.Unknown;

    if (selectedVarName === VarName.Unknown) {
      enqueueSnackbar('Please select a varName before creating an alert', {
        variant: 'error',
      });
      return;
    }

    api
      .add(checkListId, buildCreateParam(alertParam))
      .then((res) => {
        syncAlerts(res);
        enqueueSnackbar('Alert successfully created', { variant: 'success' });
      })
      .catch(handleError);
  };

  const handleDelete = (alertId: number) => {
    api
      .remove(alertId, checkListId)
      .then((res) => {
        syncAlerts(res);
        enqueueSnackbar('Alert successfully deleted', { variant: 'success' });
      })
      .catch(handleError);
  };

  // [Note]: currently we dont have update api route, so remove existing one and add new one
  const handleUpdate = (alertId: number, alertParam: AlertParam) => {
    api
      .remove(alertId, checkListId)
      .then((deleteRes) => {
        syncAlerts(deleteRes);
        return (api.add as (id: string, p: CreateParam) => Promise<Alert>)(
          checkListId,
          buildCreateParam(alertParam)
        );
      })
      .then((createRes) => {
        syncAlerts(createRes);
        enqueueSnackbar('Alert successfully updated', { variant: 'success' });
      })
      .catch((err) => {
        enqueueSnackbar('Failed to update alert — it may have been removed without a replacement', {
          variant: 'error',
        });
        handleError(err);
      });
  };

  const changeStatus = (alertParam: AlertParam) => {
    const { alertStatus } = alertParam;
    const hasExistingAlert = !!alertStatus;
    const hasActiveAlert = hasExistingAlert && alertStatus.isActive === true;

    api
      .changeStatus(alertParam.alertId, !hasActiveAlert, checkListId)
      .then((res) => {
        syncAlerts(res);
        enqueueSnackbar('Alert successfully updated', { variant: 'success' });
      })
      .catch(handleError);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex' }}>
        <NotificationsIcon sx={{ fontSize: '50px' }} color="warning" />
        <Box sx={{ ml: '10px' }}>
          <Typography variant="h6" gutterBottom>
            Configure Alerts for ({checkListId})
          </Typography>
          <p style={{ padding: 0, margin: 0 }}>Create alert rule</p>
        </Box>
      </Box>

      <Divider sx={{ border: `solid 0.5px ${theme.palette.primary.contrastText}50` }} />

      {alertParams.map((alertParam, index) => (
        <AlertParamAccordion
          checkListId={checkListId}
          alertParam={alertParam}
          isLocation={isLocation}
          onSave={handleSubmit}
          onDelete={handleDelete}
          onUpdate={handleUpdate}
          onChangeParam={(patch) => updateAlertParam(index, patch)}
          onChangeAnalysis={(patch) => updateAnalysisOptions(index, patch)}
          changeStatus={changeStatus}
          cancelHandler={(alertId: number) =>
            setAlertParams((prev) => prev.filter((param) => param.alertId !== alertId))
          }
        />
      ))}

      <Button
        onClick={addAlertParam}
        variant={themeProps.btnVariant.default}
        color="success"
        startIcon={<AddIcon />}
        size="small"
        sx={{ textTransform: 'capitalize', mt: 2 }}
      >
        Add Alert Param
      </Button>
    </Paper>
  );
}

AlertConfig.defaultProps = {
  params: undefined,
  setParams: undefined,
};

export default AlertConfig;
